import { lazy, Suspense, useMemo } from 'react';
import { prefersReducedMotion } from '@/lib/device';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import ScrollProgress from '@/components/ScrollProgress';
import SchemaMarkup from '@/components/SchemaMarkup';
import { Seo } from '@/components/Seo';

// Lazy load below-fold sections to reduce initial bundle size
const Marquee = lazy(() => import('@/components/Marquee'));
const ValueProps = lazy(() => import('@/components/ValueProps'));
const Cases = lazy(() => import('@/components/Cases'));
const Pain = lazy(() => import('@/components/Pain'));
const Services = lazy(() => import('@/components/Services'));
const Marketing = lazy(() => import('@/components/Marketing'));
const Process = lazy(() => import('@/components/Process'));
const About = lazy(() => import('@/components/About'));
const MaxSection = lazy(() => import('@/components/MaxSection'));
const BlogPreview = lazy(() => import('@/components/BlogPreview'));
const Contact = lazy(() => import('@/components/Contact'));
const Footer = lazy(() => import('@/components/Footer'));
const MobileStickyCTA = lazy(() => import('@/components/MobileStickyCTA'));

// Lazy load desktop-only components
const MagneticCursor = lazy(() => import('@/components/MagneticCursor'));
const SmoothScroll = lazy(() => import('@/components/SmoothScroll'));

/**
 * Kein Preloader mehr.
 *
 * Gemessen am Live-Stand: TTFB 26 ms, DOMContentLoaded 95 ms, load 122 ms bei
 * 35 Requests / ~10 KB Transfer. Der Ladescreen zählte davon unabhängig einen
 * zufälligen Prozentbalken auf 100 hoch (Intervall 50 ms, +1..10 pro Tick),
 * also 0,5–2,5 s plus 0,5 s Halten plus 0,5 s Fade — die Seite wurde künstlich
 * um ein Vielfaches ihrer echten Ladezeit verzögert, bevor überhaupt Inhalt
 * sichtbar war. Der Hero ist der Auftritt, nicht der Zähler.
 *
 * Nebeneffekt: Desktop und Mobile rendern jetzt denselben Baum. Vorher gab es
 * zwei getrennte Render-Pfade, die auseinanderlaufen konnten.
 */
const Index = () => {
  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }, []);

  // Der Magnetic-Cursor ist eine dauerhafte, dem Zeiger nachlaufende Bewegung
  // per JS-Transform — vom Reduced-Motion-Block in index.css nicht erfasst.
  const reduceMotion = useMemo(() => prefersReducedMotion(), []);

  // overflow-x-clip statt -hidden: "hidden" macht den Wrapper zum
  // Scroll-Container, und dann klebt kein position:sticky darin mehr
  // (gestapelte Case-Karten). "clip" schneidet genauso ab, ohne das.
  const content = (
    <div className="min-h-screen text-foreground overflow-x-clip">
      {/*
        Sprungmarke fuer Tastatur- und Screenreader-Nutzer (WCAG 2.4.1).
        Die Startseite ist ueber 17.000 px lang; ohne diesen Link fuehrt der
        Weg zum Inhalt bei jedem Seitenaufruf durch die komplette Navigation.
        Sichtbar erst bei Fokus — deshalb sr-only plus focus:not-sr-only.

        Steht bewusst VOR der Navbar: Als spaeteres Tab-Ziel waere er
        wirkungslos, weil der erste Tab dann schon in der Navigation landet.
      */}
      <a
        href="#inhalt"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-3 focus:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
      >
        Zum Inhalt springen
      </a>
      {!isMobile && !reduceMotion && (
        <Suspense fallback={null}>
          <MagneticCursor />
        </Suspense>
      )}
      <ScrollProgress />
      <Navbar />
      <main id="inhalt" tabIndex={-1}>
        <Hero isLoaded={true} />
        {/*
          Platzhalter statt `null`: createRoot ersetzt das prerenderte HTML,
          die lazy Sektionen sind danach kurz leer. Lud der Footer (eigene
          Suspense-Grenze) vor ihnen, stand er direkt unter dem Hero im
          sichtbaren Bereich und wurde anschließend nach unten geschoben —
          gemessen CLS 0,156 in einem von vier Lighthouse-Läufen. Mit einem
          Bildschirm Mindesthöhe liegt er bis dahin außerhalb des Viewports;
          Verschiebungen dort zählen nicht.
        */}
        <Suspense fallback={<div aria-hidden="true" className="min-h-screen" />}>
          <Marquee />
          <ValueProps />
          <Cases />
          <Pain />
          <Services />
          <Marketing />
          <Process />
          <About />
          <MaxSection />
          <BlogPreview />
          <Contact />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
        {isMobile && <MobileStickyCTA />}
      </Suspense>
    </div>
  );

  return (
    <>
      <SchemaMarkup />
      <Seo
        title="beyerdigital — Shopify & Webentwicklung Ludwigsburg"
        description="Ihre Digitalagentur für Ludwigsburg & Stuttgart: Performante Shopify-Shops, moderne Next.js Web-Apps und KI-Automatisierung für den Mittelstand in Baden-Württemberg."
        path="/"
      />
      {isMobile ? (
        content
      ) : (
        // fallback bewusst null und nicht `content`: sonst würde der Baum erst
        // ohne und dann innerhalb von SmoothScroll gemountet — GSAP-Kontexte und
        // Bilder würden doppelt initialisiert.
        <Suspense fallback={null}>
          <SmoothScroll>{content}</SmoothScroll>
        </Suspense>
      )}
    </>
  );
};

export default Index;
